import type { Query } from '@tanstack/react-query';

/**
 * Query keys for the conversations API.
 *
 * The inbox tabs keep the inquiry lists' keys, `['inquiriesV2', tab, userId,
 * cancerTypeId]`, because their pages keep the inquiry lists' `inquiries`, so
 * every existing patch to those lists still lands. A single inquiry also lives
 * under `['inquiriesV2', conversationId]`, which is why the inbox is matched by
 * the list key's length rather than by its root alone. A tab grouped by question adds `'question'`
 * as a fifth element.
 */
export const conversationKeys = {
  threads: ['supportThread'] as const,
  thread: (conversationId: string) => ['supportThread', conversationId] as const,
};

const INBOX_LIST_KEY_LENGTH = 4;

const GROUPED_INBOX_LIST_KEY_LENGTH = 5;

/**
 * An inbox tab, flat or grouped by question. The grouped key is the flat one with the grouping on
 * the end, so both carry pages of the same shape and the same patches apply to either.
 */
export function isInboxListQuery(query: Pick<Query, 'queryKey'>): boolean {
  const { queryKey } = query;

  return (
    queryKey[0] === 'inquiriesV2' &&
    (queryKey.length === INBOX_LIST_KEY_LENGTH ||
      (queryKey.length === GROUPED_INBOX_LIST_KEY_LENGTH && queryKey[4] === 'question'))
  );
}

/**
 * One tab of the inbox for one viewer, flat or grouped by question, whatever the cancer type filter.
 * The tab is the key's second element, `open` or `closed`.
 */
export function isInboxTabListQuery(
  query: Pick<Query, 'queryKey'>,
  tab: 'open' | 'closed',
  userId: string | undefined
): boolean {
  return isInboxListQuery(query) && query.queryKey[1] === tab && query.queryKey[2] === userId;
}
